import React from "react";
import { connect } from "react-redux";
import { Badge, Button, Card, Col, Nav, Row, Tab, Table } from "react-bootstrap";
import PropTypes from "prop-types";

import FileUi from "./ui";

import { sharedWithMe as getSharedWithMe, download as downloadFile } from "../../actions/files";
import { send as pageInfo, loading } from "../../actions/pages";

const formatSize = (value) => {
    const size = Number(value || 0);
    if (!size) return "";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
    return `${(size / 1024 / 1024 / 1024).toFixed(1)} GB`;
};

const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleDateString();
};

const EmptyState = ({ label }) => (
    <div className="text-center text-muted py-5">
        {label}
    </div>
);

const FileTable = ({ items, onDownload }) => (
    <Table responsive hover className="mb-0">
        <thead>
            <tr>
                <th>Name</th>
                <th style={{ width: "120px" }}>Type</th>
                <th style={{ width: "120px" }}>Size</th>
                <th style={{ width: "140px" }}>Updated</th>
                <th className="text-end" style={{ width: "140px" }} />
            </tr>
        </thead>
        <tbody>
            {items.map((item) => (
                <tr key={`${item.type}-${item.uuid}`}>
                    <td>
                        <div className="fw-medium">{item.name}</div>
                        {item.path ? <div className="small text-muted">{item.path}</div> : null}
                    </td>
                    <td>
                        <Badge bg={item.type === "directory" ? "secondary" : "primary"}>
                            {item.type === "directory" ? "Folder" : "File"}
                        </Badge>
                    </td>
                    <td>{item.type === "file" ? formatSize(item.size) : ""}</td>
                    <td>{formatDate(item.updatedAt)}</td>
                    <td className="text-end">
                        {item.type === "file" ? (
                            <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => onDownload(item)}
                            >
                                Download
                            </Button>
                        ) : null}
                    </td>
                </tr>
            ))}
        </tbody>
    </Table>
);

class Files extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selected: "mine",
            mine: [],
            shared: []
        };
    }

    componentDidMount = async () => {
        await this.loadData();
    };

    loadData = async () => {
        this.props.loading(true);

        try {
            const [mine, shared] = await Promise.all([
                Promise.resolve({ items: [] }),
                this.props.getSharedWithMe()
            ]);

            this.setState({
                mine: mine?.items || [],
                shared: shared?.items || []
            });

            await this.props.pageInfo({
                title: "My Files",
                breadcrumb: "Files",
                description: "Files owned by or shared to you"
            });
        } finally {
            this.props.loading(false);
        }
    };

    onTabChange = (selected) => {
        this.setState({ selected });
    };

    onDownload = async (item) => {
        await this.props.downloadFile({
            uuid: item.uuid,
            name: item.name
        });
    };

    renderTable = (items, emptyLabel) => (
        <Card body className="mb-5">
            {items.length ? (
                <FileTable items={items} onDownload={this.onDownload} />
            ) : (
                <EmptyState label={emptyLabel} />
            )}
        </Card>
    );

    render() {
        const personUuid = this.props.auth?.user?.uuid;

        return (
            <>
                <Col md="8" className="float-right text-end" />

                <Tab.Container activeKey={this.state.selected}>
                    <Card.Header className="border-0 pb-0" style={{ backgroundColor: "white" }}>
                        <Nav className="nav-tabs-line card-header-tabs" variant="tabs" activeKey={this.state.selected} style={{ backgroundColor: "white" }}>
                            <Nav.Item className="text-center">
                                <Nav.Link
                                    href="#mine"
                                    className={`${this.state.selected === "mine" && "active"}`}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        this.onTabChange("mine");
                                    }}
                                    style={{ paddingTop: "25px", paddingBottom: "25px" }}
                                >
                                    My Files
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item className="text-center">
                                <Nav.Link
                                    href="#shared"
                                    className={`${this.state.selected === "shared" && "active"}`}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        this.onTabChange("shared");
                                    }}
                                    style={{ paddingTop: "25px", paddingBottom: "25px" }}
                                >
                                    Shared With Me
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </Card.Header>
                    <Col md="12">
                        <Row>
                            <Col>
                                <Tab.Content className="h-100" style={{ minHeight: "50vh" }}>
                                    <Tab.Pane active={this.state.selected === "mine"} className="h-100 scroll-out">
                                        {personUuid ? (
                                            <Card body className="mb-5">
                                                <FileUi
                                                    ownerType="person"
                                                    ownerUuid={personUuid}
                                                />
                                            </Card>
                                        ) : (
                                            this.renderTable(this.state.mine, "Unable to load your file owner.")
                                        )}
                                    </Tab.Pane>
                                    <Tab.Pane active={this.state.selected === "shared"} className="h-100 scroll-out">
                                        {this.renderTable(this.state.shared, "No files have been shared with you yet.")}
                                    </Tab.Pane>
                                </Tab.Content>
                            </Col>
                        </Row>
                    </Col>
                </Tab.Container>
            </>
        );
    }
}

Files.propTypes = {
    auth: PropTypes.object,
    getSharedWithMe: PropTypes.func.isRequired,
    downloadFile: PropTypes.func.isRequired,
    pageInfo: PropTypes.func.isRequired,
    loading: PropTypes.func.isRequired
};

Files.defaultProps = {
    auth: {}
};

const mapStateToProps = (state) => ({
    auth: state.auth || {}
});

export default connect(mapStateToProps, { getSharedWithMe, downloadFile, pageInfo, loading })(Files);
